import {
    Completable,
    completed,
    isVerified,
    NameVerificationDTO,
    pending,
    UsersWebDTO,
} from 'kashable-mobile-common';

import {fetchUser} from '../apis/users';
import {RootState} from '../store';
import { MfaType } from '../types/MfaType';
import { addStateHistory } from '../utils/StateHistoryUtil';
import {gtmPushEvent} from '../utils/TrackUtil';

import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';

const INITIAL_STATE: Completable<UsersWebDTO> = pending();

export const fetchProfile = createAsyncThunk('user/fetch', async (user, {rejectWithValue}) => {
    const response = await fetchUser();

    if (response.error) {
        rejectWithValue(response);
    }

    if (response.value) {
        // Sentry.setUser(response.value as User);
    }

    return response;
});

const trackVerifyComplete = (user: UsersWebDTO) => {
    if (isVerified(user)) {
        gtmPushEvent('verify-complete');
    }
};

export const userSlice = createSlice({
    name: 'user',
    initialState: INITIAL_STATE,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        verifyEmail: (state) => {
            if (state.value) {
                const _user = {...state.value};
                _user.email_verified = true;
                return completed(_user);
            }
            return state;
        },
        verifyPhone: (state) => {
            if (state.value) {
                const _user = {...state.value};
                _user.phone_verified = true;
                return completed(_user);
            }
            return state;
        },
        verifyEmployment: (state) => {
            if (state.value) {
                const _user = {...state.value};
                _user.employment_verified = true;

                trackVerifyComplete(_user);
                return completed(_user);
            }
            return state;
        },
        verifyName: (state, action: PayloadAction<NameVerificationDTO>) => {
            if (state.value) {
                const _user = {...state.value};
                _user.first_name = action.payload.first_name;
                _user.last_name = action.payload.last_name;
                return completed(_user);
            }
            return state;
        },
        verifyMfa:  (state) => {
            if (state.value) {
                const _user = {...state.value};
                _user.mfa_verified = true;
                return completed(_user);
            }
        },
        addMfa: (state, action:PayloadAction<MfaType>) => {
            if (state.value) {
                const _user = {...state.value};
                _user.mfa_type = action.payload;
                return completed(_user);
            }
        },
        markPii: (state) => {
            if (state.value) {
                const _user = {...state.value};
                _user.pii = true;
                return completed(_user);
            }
        },
        removeMfa: (state) => {
            if (state.value) {
                const _user = {...state.value};
                _user.mfa_type = undefined;
                return completed(_user);
            }
        },
        update(state, action: PayloadAction<Partial<UsersWebDTO>>) {
            if (state.value) {
                return completed({ ...state.value, ...action.payload });
            }
            return state;
        },
        updateFullName(state, action: PayloadAction<NameVerificationDTO>) {
            if (state.value) {
                const _user = {...state.value};
                _user.first_name = action.payload.first_name;
                _user.last_name = action.payload.last_name;
                return completed(_user);
            }
            return state;
        },
        updatePhoneNumber(state, action: PayloadAction<string>) {
            if (state.value) {
                const _user = {...state.value};
                _user.phone = action.payload;
                addStateHistory('updatePhoneNumber');
                return completed(_user);
            } else {
                addStateHistory('updatePhoneNumber - no value');
            }
            return state;
        },
        updateEmailAddress(state, action: PayloadAction<string>) {
            if (state.value) {
                const _user = {...state.value};
                _user.email = action.payload;
                return completed(_user);
            }
            return state;
        },
        clearUser: () => {
            return {...INITIAL_STATE};
        },
        resetEmploymentVerification(state){
            if(state.value){
                state.value.employment_verified = false;
            }

            return state;
        },
        setAccountBreached: (state) => {
            if (state.value) {
                const _user = {...state.value};
                _user.account_breached = true;
                return completed(_user);
            }
        },
        clearAccountBreached: (state) => {
            if (state.value) {
                const _user = {...state.value};
                _user.account_breached = false;
                return completed(_user);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProfile.pending, () => {
                return pending();
            })
            .addCase(fetchProfile.fulfilled, (state, action) => {
                return action.payload;
            });
    },
});

export const selectUser = (state: RootState) => state.user;

export const {
    verifyEmail,
    verifyPhone,
    verifyEmployment,
    verifyName,
    verifyMfa,
    removeMfa,
    addMfa,
    update,
    markPii,
    updatePhoneNumber,
    updateEmailAddress,
    updateFullName,
    clearUser,
    resetEmploymentVerification,
    setAccountBreached,
    clearAccountBreached,
} = userSlice.actions;
export default userSlice.reducer;
